import { html } from "lit";

import { customElement, property } from "lit/decorators.js";

import { SignalElement, inject } from "ui-common/di";
import type { IAuthState, IAuthService } from "ui-common/services/auth/types";

import { styles } from "./app-menu.css";
import { DESKTOP_MENU_ITEMS, FAN_MENU_ITEMS } from "./menu-items";
import type { MenuItem } from "./types";

import { gbFlag } from "ui-components/menu-button/flag-gb.svg";
import { nlFlag } from "ui-components/menu-button/flag-nl.svg";
import "ui-components/tab-menu";
import { featureFlag } from "ui-components/wow-feature/wow-feature";
import { ThemeController, type Theme } from "../../../../../libs/ui-common/theme";
import type { IBackofficeFeatureFlagsState } from "../../services/backoffice-feature-flags/types";
import type { RoutePath } from "../../router";
import { navigateTo } from "../../router";
import { LanguageController, t, type Language } from "../../translations/i18n";

type CombinedRoutePath = RoutePath | "feed" | "explore";

@customElement("app-menu")
export class AppMenu extends SignalElement {
  @inject<IAuthState>("authState")
  private authState!: IAuthState;

  @inject<IAuthService>("authService")
  private authService!: IAuthService;

  @inject<IBackofficeFeatureFlagsState>("backofficeFeatureFlagsState")
  private backofficeFeatureFlagsState!: IBackofficeFeatureFlagsState;

  static override styles = styles;

  private themeController = new ThemeController(this);
  private languageController = new LanguageController(this);

  @property({ type: String })
  private _currentPath?: CombinedRoutePath;

  override connectedCallback() {
    super.connectedCallback();

    this.setCurrentPathFromUrl();
    // Listen for route changes to update active state
    window.addEventListener("popstate", this.handleRouteChange);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener("popstate", this.handleRouteChange);
  }

  private setCurrentPathFromUrl() {
    // Extract the route path, removing the app prefix (backoffice)
    const appPath = window.location.pathname.substring(1).split("/").slice(1).join("/");
    this._currentPath = appPath as RoutePath;
  }

  private handleRouteChange = () => {
    this.setCurrentPathFromUrl();
    this.requestUpdate();
  };

  private _isActive(path: CombinedRoutePath): boolean {
    if (this._currentPath === path) {
      return true;
    }

    // Auto-detect if current path is a subpage of this menu item
    // e.g., "posts/worlds" should activate "posts" menu item
    return this._currentPath?.startsWith(path + "/") || false;
  }

  private _handleThemeToggle(theme: Theme) {
    this.themeController.setTheme(theme);
  }

  private _renderMenuSection(items: MenuItem[]) {
    return html`
      <div class="menu-section">
        ${items.map(
          (item) => html`
            <a
              href="/${item.path}"
              class="menu-item ${this._isActive(item.path) ? "active" : ""}"
              @click=${this.getItemClickHandler(item)}
            >
              <span class="icon">${item.icon}</span>
              <span>${item.label}</span>
            </a>
          `,
        )}
      </div>
    `;
  }

  getItemClickHandler(item: MenuItem) {
    return async (e: MouseEvent) => {
      e.preventDefault();

      if (["feed", "explore"].includes(item.path)) {
        return window.open(`/${item.path}`, "_self", "noopener,noreferrer");
      }

      const path = item.path as RoutePath;
      const success = await navigateTo(path);

      if (!success) return;

      this._currentPath = item.path;
    };
  }

  private _handleLanguageToggle(language: Language) {
    this.languageController.setLanguage(language);
  }

  override render() {
    const currentUser = this.authState.currentUser.get();
    const userHandle = currentUser?.handle;

    const isCreator = !!userHandle;

    const isFan = !!currentUser?.id && !isCreator;

    const hasExplore = this.backofficeFeatureFlagsState.hasFeature("explore");
    const hasMessages = this.backofficeFeatureFlagsState.hasFeature("messages");
    const hasCommunities = this.backofficeFeatureFlagsState.hasFeature("communities");

    // Filter menu items based on feature flags
    const filteredDesktopMenuItems = DESKTOP_MENU_ITEMS.filter((item) => {
      if (item.path === "explore") {
        return hasExplore;
      }
      if (item.path === "messages") {
        return hasMessages;
      }
      if (item.path === "communities") {
        return hasCommunities;
      }
      return true;
    });

    const filteredFanMenuItems = FAN_MENU_ITEMS.filter((item) => {
      if (item.path === "explore") {
        return hasExplore;
      }
      return true;
    });

    const creatorModalLinks = [
      {
        label: t("creator-modal.editProfile"),
        description: t("creator-modal.profileDescription"),
        href: "/my/profile",
        icon: html`<ph-user size="22" slot="icon"></ph-user>`,
      },
      {
        label: t("creator-modal.editLinks"),
        description: t("creator-modal.linksDescription"),
        href: "/my/links",
        icon: html`<ph-link size="22" slot="icon"></ph-link>`,
      },
      {
        label: t("creator-modal.editWorlds"),
        description: t("creator-modal.worldsDescription"),
        href: "/my/worlds",
        icon: html`<ph-images size="22" slot="icon"></ph-images>`,
      },
      {
        label: t("creator-modal.editLiveEvents"),
        description: t("creator-modal.liveEventsDescription"),
        href: "/my/live-events",
        icon: html`<ph-video-camera size="22" slot="icon"></ph-video-camera>`,
      },
    ];

    if (hasCommunities) {
      creatorModalLinks.push({
        label: t("creator-modal.editCommunities"),
        description: t("creator-modal.communitiesDescription"),
        href: "/my/communities",
        icon: html`<ph-users size="22" slot="icon"></ph-users>`,
      });
    }

    if (hasMessages) {
      creatorModalLinks.push({
        label: t("creator-modal.editMessages"),
        description: t("creator-modal.messagesDescription"),
        href: "/my/messages",
        icon: html`<ph-paper-plane-tilt size="22" slot="icon"></ph-paper-plane-tilt>`,
      });
    }

    return html`
      <section class="layout">
        <div class="logo">
          <app-logo class="show-on-desktop"></app-logo>
        </div>
        ${featureFlag(
          isCreator,
          html`<nav class="menu-list menu-list-desktop">${this._renderMenuSection(filteredDesktopMenuItems)}</nav>
            <tab-menu
              class="menu-list-mobile"
              .showExplore=${hasExplore}
              .createManageOptions=${{
                header: t("creator-modal.manageCreate"),
                links: creatorModalLinks,
              }}
              .labels=${{ feed: t("menu.feed"), explore: t("menu.explore") }}
            ></tab-menu>`,
        )}
        ${featureFlag(
          isFan,
          html`<nav class="menu-list menu-list-desktop">${this._renderMenuSection(filteredFanMenuItems)}</nav>
            <tab-menu
              class="menu-list-mobile"
              .showExplore=${hasExplore}
              .createManageOptions=${{
                header: t("creator-modal.manageCreate"),
                links: [
                  {
                    label: t("creator-modal.editProfile"),
                    description: t("creator-modal.profileDescription"),
                    href: "/my/profile",
                    icon: html`<ph-user size="22" slot="icon"></ph-user>`,
                  },
                  {
                    label: t("creator-modal.editLinks"),
                    description: t("creator-modal.linksDescription"),
                    href: "/my/links",
                    icon: html`<ph-link size="22" slot="icon"></ph-link>`,
                  },
                  {
                    label: t("creator-modal.editWorlds"),
                    description: t("creator-modal.worldsDescription"),
                    href: "/my/worlds",
                    icon: html`<ph-images size="22" slot="icon"></ph-images>`,
                  },
                ],
              }}
              .labels=${{ feed: t("menu.feed"), explore: t("menu.explore") }}
            ></tab-menu>`,
        )}
        <div class="bottom-section">
          <div class="toggle-selections">
            <div class="language-buttons">
              <button
                class="language-button ${this.languageController.language === "en" ? "active" : ""}"
                @click=${() => this._handleLanguageToggle("en")}
                aria-label=${t("language.selectEnglish")}
              >
                ${gbFlag}
              </button>
              <button
                class="language-button ${this.languageController.language === "nl" ? "active" : ""}"
                @click=${() => this._handleLanguageToggle("nl")}
                aria-label=${t("language.selectDutch")}
              >
                ${nlFlag}
              </button>
            </div>
            <div class="theme-toggles">
              <button
                class="theme-toggle ${this.themeController.resolvedTheme === "light" ? "active" : ""}"
                title=${t("theme.lightTheme")}
                @click=${() => this._handleThemeToggle("light")}
              >
                <span class="icon"><ph-sun weight="bold" size="12px"></ph-sun></span>
              </button>
              <button
                class="theme-toggle ${this.themeController.resolvedTheme === "dark" ? "active" : ""}"
                title=${t("theme.darkTheme")}
                @click=${() => this._handleThemeToggle("dark")}
              >
                <span class="icon"><ph-moon weight="bold" size="12px"></ph-moon></span>
              </button>
            </div>
          </div>

          <wow-button @click=${() => this.authService.signOut()} variant="subtle">
            <ph-sign-out weight="bold" size="20"></ph-sign-out>${t("menu.signOut")}
          </wow-button>
        </div>
      </section>
    `;
  }
}
